import type { PixelEvent } from '@/events';
import type { PixelLogger } from '@/logger';

import type { IBatchManager, BatchConfig } from '../batch/types';
import type { IFlushManager } from '../flush/types';
import type { IOfflineDetector } from '../network/types';
import type { IRetryEngine } from '../retry/types';
import type { EventSender } from '../sender/types';

export type DeliveryConfig = {
  readonly batch: BatchConfig;
  readonly timeoutMs: number;
};

export type DeliveryDebugState = {
  readonly batch: ReturnType<IBatchManager['getState']>;
  readonly retry: ReturnType<IRetryEngine['getState']>;
  readonly offline: ReturnType<IOfflineDetector['getState']>;
  readonly flush: ReturnType<IFlushManager['getState']>;
  readonly timeoutMs: number;
  readonly inFlight: boolean;
  readonly ackCount: number;
};

export class TransportDelivery {
  private readonly batchManager: IBatchManager;

  private readonly flushManager: IFlushManager;

  private readonly retryEngine: IRetryEngine;

  private readonly offlineDetector: IOfflineDetector;

  private readonly sender: EventSender;

  private readonly config: DeliveryConfig;

  private readonly logger: PixelLogger;

  private active = false;

  private inFlight = false;

  private ackCount = 0;

  private abortPending: (() => void) | null = null;

  private maxWaitTimer: ReturnType<typeof setTimeout> | null = null;

  private readonly onPermanentFailure:
    | ((events: readonly PixelEvent[], error: unknown) => void)
    | null;

  constructor(deps: {
    batchManager: IBatchManager;
    flushManager: IFlushManager;
    retryEngine: IRetryEngine;
    offlineDetector: IOfflineDetector;
    sender: EventSender;
    config: DeliveryConfig;
    logger: PixelLogger;
    abortPending?: () => void;
    onPermanentFailure?: (
      events: readonly PixelEvent[],
      error: unknown
    ) => void;
  }) {
    this.batchManager = deps.batchManager;
    this.flushManager = deps.flushManager;
    this.retryEngine = deps.retryEngine;
    this.offlineDetector = deps.offlineDetector;
    this.sender = deps.sender;
    this.config = deps.config;
    this.logger = deps.logger;
    this.abortPending = deps.abortPending ?? null;
    this.onPermanentFailure = deps.onPermanentFailure ?? null;
  }

  init(): void {
    if (this.active) return;

    this.offlineDetector.init(
      () => {
        this.logger.debug('[TransportDelivery] Online — resuming');
        this.flush();
      },
      () => {
        this.logger.debug('[TransportDelivery] Offline — paused');
      }
    );

    this.flushManager.init(() => this.flush());
    this.active = true;
  }

  destroy(): void {
    if (!this.active) return;

    this.abortPending?.();
    this.retryEngine.cancelAll();
    this.flushManager.destroy();
    this.offlineDetector.destroy();
    this.clearMaxWaitTimer();
    this.batchManager.clear();
    this.active = false;
    this.inFlight = false;
  }

  ingest(events: readonly PixelEvent[]): void {
    if (!this.active || events.length === 0) return;

    this.batchManager.add(events);

    if (this.batchManager.shouldFlush(this.config.batch)) {
      this.flush();
    } else {
      this.scheduleMaxWaitFlush();
    }
  }

  flush(): void {
    if (!this.active || !this.offlineDetector.isOnline()) return;
    this.clearMaxWaitTimer();
    void this.processBatch();
  }

  getDebugState(): DeliveryDebugState {
    return {
      batch: this.batchManager.getState(),
      retry: this.retryEngine.getState(),
      offline: this.offlineDetector.getState(),
      flush: this.flushManager.getState(),
      timeoutMs: this.config.timeoutMs,
      inFlight: this.inFlight,
      ackCount: this.ackCount,
    };
  }

  getOfflineDetector(): IOfflineDetector {
    return this.offlineDetector;
  }

  private async processBatch(): Promise<void> {
    if (this.inFlight || !this.offlineDetector.isOnline()) return;

    const batch = this.batchManager.takeBatch(
      this.config.batch.enabled ? this.config.batch.batchSize : 1
    );
    if (batch.length === 0) return;

    this.inFlight = true;

    try {
      await this.retryEngine.retry(async () => {
        const result = await this.sender.send(batch, {
          deliveryMode:
            this.config.batch.enabled && batch.length > 1 ? 'batch' : 'single',
        });
        if (!result.ack) {
          throw new Error('[TransportDelivery] Send not acknowledged');
        }
        this.ackCount += result.eventCount;
      }, `delivery-${Date.now()}`);
    } catch (error) {
      this.logger.error(
        '[TransportDelivery] Delivery failed after retries',
        error
      );

      if (this.onPermanentFailure) {
        this.onPermanentFailure(batch, error);
      } else {
        this.batchManager.add(batch);
      }
    } finally {
      this.inFlight = false;

      if (this.batchManager.size() > 0 && this.offlineDetector.isOnline()) {
        this.flush();
      }
    }
  }

  private scheduleMaxWaitFlush(): void {
    if (
      !this.config.batch.enabled ||
      this.maxWaitTimer !== null ||
      this.config.batch.maxWaitMs <= 0
    ) {
      if (this.config.batch.maxWaitMs <= 0) this.flush();
      return;
    }

    this.maxWaitTimer = setTimeout(() => {
      this.maxWaitTimer = null;
      this.flush();
    }, this.config.batch.maxWaitMs);
  }

  private clearMaxWaitTimer(): void {
    if (this.maxWaitTimer === null) return;
    clearTimeout(this.maxWaitTimer);
    this.maxWaitTimer = null;
  }
}
